// Admin API wrapper for /question-set endpoint.
// Reused for admin-created public tests (PYPs and general public tests).
// See docs/FeatureDesigns/assignments-tests/admin-public-tests.md
import type { QuestionSet, QuestionSetConfig, QuestionSetSection, ExamPaperRef, QuestionSetPurpose, QuestionSetModality, QuestionSetPYQType } from '@shared-ui/question-set-types';
import type { MarkingScheme, ExamInterfaceInput, WorkingSetting } from '@padcanvas/shared';
import type { QuestionDiffResult } from '@padcanvas/shared';
import { fetchWithAdmin } from './fetchHelper';

/**
 * The play config as an EDITOR may send it, which is one value wider than what
 * is stored: `examInterface` also accepts `'auto'`, meaning "go back to deriving
 * it". The server deletes the field on `'auto'` and never writes it, so the
 * stored type (`QuestionSetConfig`) rightly does not admit the value — this is
 * the wire shape, and naming it keeps the two apart instead of casting at the
 * call site. See docs/FeatureDesigns/assignments-tests/exam-interface-target.md.
 * `working` likewise takes `'default'`, meaning "back to the tenant's default".
 */
type QuestionSetConfigInput =
  Partial<Omit<QuestionSetConfig, 'examInterface' | 'working'>>
  & { examInterface?: ExamInterfaceInput; working?: WorkingSetting | 'default' };

// Result of applying a test-doc import (question-set/import).
export interface TestImportResult {
  created: number;
  updated: number;
  unchanged: number;
  skipped: number;
  errors: number;
  warnings?: string[];
  results: { id: string; title: string; status: 'created' | 'updated' | 'unchanged' | 'skipped' | 'error'; reason?: string }[];
}

interface CreateArgs {
  title: string;
  questionIds: string[];
  /** Phase 2: section structure (questionIds derived from these). */
  sections?: QuestionSetSection[];
  /** Tier 2: test-level default marking rule. */
  marking?: MarkingScheme;
  config?: QuestionSetConfigInput;
  visibility: 'public' | 'private';
  description?: string;
  tags?: string[];
  type?: 'test' | 'practice-set';
  targetExam?: string;
  targetExams?: string[];       // topic exercises: multi-exam targeting
  classes?: string[];
  isPyp?: boolean;
  pypRef?: ExamPaperRef;
  source?: string;
  status?: 'draft' | 'published';
  // ── Dimensional fields (P1) — see qset-model-target.md ──
  // ownerType is server-derived; never sent from the client.
  purpose?: QuestionSetPurpose;                 // 'exam-paper' | 'topic-exercise' | 'custom'
  pyqType?: QuestionSetPYQType;                 // creator-asserted PYQ claim (FullPYP/MixedPYP/None), §3f
  /** The authored chapter/topic claim. The topic-exercise editor's picker still
   *  calls it "the anchor"; the stored field is `topics`, which `anchorModuleId`
   *  retired into. See qset-model-target.md §Module association. */
  topics?: string[];
  difficulty?: 'easy' | 'medium' | 'hard';       // exercise difficulty tier (discovery ordering)
  sortOrder?: number;                            // optional manual ordering within a topic
  defaultModality?: QuestionSetModality;         // default play mode
  allowedModalities?: QuestionSetModality[];     // modes the learner may choose (>=1)
}

interface UpdateArgs {
  questionSetId: string;
  title?: string;
  description?: string;
  /** Flat sets only: replace `questionIds` with this exact, ordered list
   *  (reorder + add/remove in one). Ignored when `sections` are non-empty. */
  questionIds?: string[];
  /** Phase 2: replace sections (non-empty) or clear to flat (empty array). */
  sections?: QuestionSetSection[];
  /** Tier 2: test-level default marking (null clears). */
  marking?: MarkingScheme | null;
  config?: QuestionSetConfigInput;
  visibility?: 'public' | 'private';
  status?: 'draft' | 'published';
  tags?: string[];
  targetExam?: string;
  targetExams?: string[];       // topic exercises: multi-exam targeting (empty clears to generic)
  classes?: string[];
  isPyp?: boolean;
  pyqType?: QuestionSetPYQType;                 // creator-asserted PYQ claim, §3f
  pypRef?: ExamPaperRef;
  source?: string;
}

interface ListArgs {
  type?: 'test' | 'practice-set';
  creatorOnly?: boolean;
  class?: string;
  subject?: string;
  exam?: string;
  isPyp?: boolean;
  /** A LIST means "any of these" — the library asks for the union of the
   *  kinds it shows. See kindQuery in @padcanvas/shared. */
  purpose?: string | readonly string[];
  /** The asserted PYQ claim, optionally negated: 'MixedPYP', 'not-FullPYP'. */
  pyqType?: string;
  publicOnly?: boolean;
  search?: string;
  subjectsAny?: string[];
  status?: 'draft' | 'published';
  /** When `pageSize` is present, the response is one page (1-based) plus `total`. */
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export const questionSetApi = {
  list: (args: ListArgs = {}) =>
    fetchWithAdmin<{ questionSets: QuestionSet[]; total?: number }>('/question-set', {
      method: 'POST',
      body: JSON.stringify({ action: 'list', ...args }),
    }),

  get: (questionSetId: string) =>
    fetchWithAdmin<{ questionSet: QuestionSet; canEdit?: boolean }>('/question-set', {
      method: 'POST',
      body: JSON.stringify({ action: 'get', questionSetId }),
    }),

  /** P5: reserve (or release) a paper for tests — hides its questions from practice until attempted. */
  setPracticeLock: (questionSetId: string, locked: boolean) =>
    fetchWithAdmin<{ questionSetId: string; locked: boolean; stamped: number }>(`/system/question-sets/${questionSetId}/practice-lock`, {
      method: 'POST',
      body: JSON.stringify({ locked }),
    }),

  create: (data: CreateArgs) =>
    fetchWithAdmin<{ questionSet: QuestionSet }>('/question-set', {
      method: 'POST',
      body: JSON.stringify({ action: 'create', ...data }),
    }),

  update: (data: UpdateArgs) =>
    fetchWithAdmin<{ questionSet: QuestionSet }>('/question-set', {
      method: 'POST',
      body: JSON.stringify({ action: 'update', ...data }),
    }),

  addQuestions: (questionSetId: string, questionIds: string[]) =>
    fetchWithAdmin<{ questionSet: QuestionSet }>('/question-set', {
      method: 'POST',
      body: JSON.stringify({ action: 'add-questions', questionSetId, questionIds }),
    }),

  removeQuestions: (questionSetId: string, questionIds: string[]) =>
    fetchWithAdmin<{ questionSet: QuestionSet }>('/question-set', {
      method: 'POST',
      body: JSON.stringify({ action: 'remove-questions', questionSetId, questionIds }),
    }),

  remove: (questionSetId: string) =>
    fetchWithAdmin<{ ok: boolean }>('/question-set', {
      method: 'POST',
      body: JSON.stringify({ action: 'delete', questionSetId }),
    }),

  // ── Cross-environment migration (export/import the test DOCS) ──────────
  // Questions are NOT moved here — they go through the Questions-page wizard.

  exportByIds: (ids: string[]) =>
    fetchWithAdmin<{ questionSets: Record<string, unknown>[] }>('/question-set/export-by-ids', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),

  previewImport: (questionSets: Record<string, unknown>[]) =>
    fetchWithAdmin<QuestionDiffResult>('/question-set/import/preview', {
      method: 'POST',
      body: JSON.stringify({ questionSets }),
    }),

  applyImport: (questionSets: Record<string, unknown>[], selectedIds?: string[]) =>
    fetchWithAdmin<TestImportResult>('/question-set/import', {
      method: 'POST',
      body: JSON.stringify({ questionSets, ...(selectedIds ? { selectedIds } : {}) }),
    }),
};
